/**
 * User Management Page (Admin Only)
 * Manage users: create, edit, activate/deactivate, reset password
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  UserCirclePlus,
  ShieldCheck,
  ShieldSlash,
  Key,
  CheckCircle,
  XCircle,
  Eye,
  EyeSlash,
  X,
  Warning,
  ArrowsClockwise,
  User,
  Envelope,
  Phone,
  Calendar,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../utils/authApi';
import { formatRelativeDate } from '../utils/formatters';

// Role badge colors
const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  user: 'bg-blue-100 text-blue-700 border-blue-200',
  analyst: 'bg-amber-100 text-amber-700 border-amber-200',
  viewer: 'bg-stone-100 text-stone-700 border-stone-200',
  partner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// Status badge colors
const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-stone-100 text-stone-500',
  suspended: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
};

// User avatar component
const UserAvatar = ({ initials, size = 'md', isActive = true }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };
  
  return (
    <div 
      className={cn(
        'rounded-full flex items-center justify-center font-medium',
        sizeClasses[size],
        isActive 
          ? 'bg-[#3A4A40] text-white' 
          : 'bg-stone-300 text-stone-600'
      )}
    >
      {initials || '?'}
    </div>
  );
};

// Create/Edit User Modal
const UserFormModal = ({ isOpen, onClose, user, onSave, isLoading }) => {
  const isEdit = !!user;
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'user',
    phone: '',
    notes: '',
    password: '',
    password_confirm: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || 'user',
        phone: user.phone || '',
        notes: user.notes || '',
        password: '',
        password_confirm: '',
      });
    } else {
      setFormData({
        full_name: '',
        email: '',
        role: 'user',
        phone: '',
        notes: '',
        password: '',
        password_confirm: '',
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Le nom est requis';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Le mot de passe doit avoir au moins 8 caractères';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Le mot de passe doit contenir une majuscule';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Le mot de passe doit contenir une minuscule';
      } else if (!/\d/.test(formData.password)) {
        newErrors.password = 'Le mot de passe doit contenir un chiffre';
      }
      
      if (formData.password !== formData.password_confirm) {
        newErrors.password_confirm = 'Les mots de passe ne correspondent pas';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const data = { ...formData };
      if (isEdit) {
        delete data.password;
        delete data.password_confirm;
      }
      onSave(data, isEdit);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="user-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <PencilSimple size={20} /> : <UserCirclePlus size={20} />}
            {isEdit ? 'Modifier l\'Utilisateur' : 'Créer un Nouvel Utilisateur'}
          </DialogTitle>
          <DialogDescription>
            {isEdit 
              ? 'Modifier les informations utilisateur. Les changements prennent effet immédiatement.' 
              : 'Ajouter un nouvel utilisateur à la plateforme.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="full_name">Nom Complet *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="Jean Dupont"
              className={errors.full_name ? 'border-red-500' : ''}
              data-testid="input-user-name"
            />
            {errors.full_name && (
              <p className="text-xs text-red-500">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john@company.com"
              className={errors.email ? 'border-red-500' : ''}
              data-testid="input-user-email"
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label>Role *</Label>
            <Select
              value={formData.role}
              onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
            >
              <SelectTrigger data-testid="select-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">Standard User</SelectItem>
                <SelectItem value="analyst">Analyst</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+33 6 12 34 56 78"
              data-testid="input-user-phone"
            />
          </div>

          {/* Password (only for create) */}
          {!isEdit && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Min. 8 chars, 1 uppercase, 1 lowercase, 1 number"
                    className={cn('pr-10', errors.password ? 'border-red-500' : '')}
                    data-testid="input-user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirm">Confirm Password *</Label>
                <Input
                  id="password_confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password_confirm}
                  onChange={(e) => setFormData(prev => ({ ...prev, password_confirm: e.target.value }))}
                  placeholder="Re-enter password"
                  className={errors.password_confirm ? 'border-red-500' : ''}
                  data-testid="input-user-password-confirm"
                />
                {errors.password_confirm && (
                  <p className="text-xs text-red-500">{errors.password_confirm}</p>
                )}
              </div>
            </>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this user..."
              rows={2}
              data-testid="input-user-notes"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-[#3A4A40] hover:bg-[#2E3B33]"
              disabled={isLoading}
              data-testid="submit-user-btn"
            >
              {isLoading ? (
                <>
                  <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                isEdit ? 'Enregistrer' : 'Créer l\'utilisateur'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Reset Password Modal
const ResetPasswordModal = ({ isOpen, onClose, user, onReset, isLoading }) => {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setPasswordConfirm('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain a lowercase letter');
      return;
    }
    if (!/\d/.test(password)) {
      setError('Password must contain a number');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match');
      return;
    }
    
    onReset(password, passwordConfirm);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] max-h-[90vh] overflow-y-auto" data-testid="reset-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={20} />
            Reset Password
          </DialogTitle>
          <DialogDescription>
            Set a new password for {user?.full_name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <Warning size={16} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="pr-10"
                data-testid="input-reset-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm Password</Label>
            <Input
              id="confirm_password"
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="Re-enter password"
              data-testid="input-reset-password-confirm"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit"
              className="bg-[#3A4A40] hover:bg-[#2E3B33]"
              disabled={isLoading}
              data-testid="submit-reset-password-btn"
            >
              {isLoading ? 'Réinitialisation...' : 'Réinitialiser'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Mobile User Card Component
const MobileUserCard = ({ user, currentUser, onEdit, onResetPassword, onToggleStatus }) => {
  return (
    <div 
      className={cn(
        "bg-card border border-border rounded-lg p-4 mb-3",
        !user.is_active && 'opacity-60'
      )}
      data-testid={`user-card-${user.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <UserAvatar initials={user.initials} isActive={user.is_active} />
          <div className="min-w-0">
            <p className="font-medium truncate">{user.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            {user.id === currentUser?.id && (
              <span className="text-xs text-muted-foreground">(You)</span>
            )}
          </div>
        </div>
        <Badge 
          variant="outline" 
          className={cn('text-xs flex-shrink-0', ROLE_COLORS[user.role])}
        >
          {user.role}
        </Badge>
      </div>

      {/* Status & Last Login */}
      <div className="flex items-center justify-between mb-3 text-sm">
        <Badge 
          variant="secondary" 
          className={cn('text-xs', STATUS_COLORS[user.status])}
        >
          {user.is_active ? (
            <CheckCircle size={12} className="mr-1" />
          ) : (
            <XCircle size={12} className="mr-1" />
          )}
          {user.status}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {user.last_login_at 
            ? formatRelativeDate(user.last_login_at)
            : 'Never logged in'
          }
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 h-9"
          onClick={() => onEdit(user)}
          data-testid={`mobile-edit-user-${user.id}`}
        >
          <PencilSimple size={14} className="mr-1" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 h-9"
          onClick={() => onResetPassword(user)}
          data-testid={`mobile-reset-password-${user.id}`}
        >
          <Key size={14} className="mr-1" />
          Password
        </Button>
        {user.id !== currentUser?.id && (
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 px-3",
              user.is_active ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'
            )}
            onClick={() => onToggleStatus(user)}
            data-testid={`mobile-toggle-status-${user.id}`}
          >
            {user.is_active ? (
              <ShieldSlash size={16} />
            ) : (
              <ShieldCheck size={16} />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

// Main component
export const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await usersApi.list(true);
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Échec du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Handle create/edit user
  const handleSaveUser = async (userData, isEdit) => {
    setIsSubmitting(true);
    try {
      if (isEdit && selectedUser) {
        await usersApi.update(selectedUser.id, userData);
        toast.success('Utilisateur mis à jour');
      } else {
        await usersApi.create(userData);
        toast.success('Utilisateur créé');
      }
      setShowUserModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || 'Opération échouée';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle activate/deactivate
  const handleToggleStatus = async (user) => {
    try {
      if (user.is_active) {
        await usersApi.deactivate(user.id);
        toast.success(`${user.full_name} désactivé`);
      } else {
        await usersApi.activate(user.id);
        toast.success(`${user.full_name} activé`);
      }
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || 'Opération échouée';
      toast.error(message);
    }
  };

  // Handle password reset
  const handleResetPassword = async (newPassword, confirmPassword) => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    try {
      await usersApi.resetPassword(selectedUser.id, newPassword, confirmPassword);
      toast.success('Mot de passe réinitialisé');
      setShowResetModal(false);
      setSelectedUser(null);
    } catch (err) {
      const message = err.response?.data?.detail || 'Échec de la réinitialisation';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  // Open reset password modal
  const openResetModal = (user) => {
    setSelectedUser(user);
    setShowResetModal(true);
  };

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.is_active).length;
  const adminUsers = users.filter(u => u.role === 'admin').length;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Users size={24} className="sm:hidden" />
            <Users size={28} className="hidden sm:block" />
            User Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage platform users and their access
          </p>
        </div>
        <Button 
          className="bg-[#3A4A40] hover:bg-[#2E3B33] w-full sm:w-auto"
          onClick={() => {
            setSelectedUser(null);
            setShowUserModal(true);
          }}
          data-testid="add-user-btn"
        >
          <Plus size={18} className="mr-1" />
          Add User
        </Button>
      </div>

      {/* Stats - Responsive grid */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total</p>
            <p className="text-lg sm:text-2xl font-bold">{totalUsers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Active</p>
            <p className="text-lg sm:text-2xl font-bold text-emerald-600">{activeUsers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Admins</p>
            <p className="text-lg sm:text-2xl font-bold text-purple-600">{adminUsers}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters - Mobile optimized */}
      <Card>
        <CardContent className="p-3 sm:pt-4">
          <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
              />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <div className="flex gap-2">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="flex-1 sm:w-[130px]" data-testid="filter-role">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 sm:w-[130px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users - Desktop Table / Mobile Cards */}
      <Card>
        <CardContent className="p-0">
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/30">
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <ArrowsClockwise size={24} className="animate-spin mx-auto text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">Loading users...</p>
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Users size={32} className="mx-auto text-muted-foreground opacity-50" />
                      <p className="mt-2 text-sm text-muted-foreground">No users found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow 
                      key={user.id} 
                      data-testid={`user-row-${user.id}`}
                      className={cn(!user.is_active && 'opacity-60')}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar initials={user.initials} isActive={user.is_active} />
                          <div>
                            <p className="font-medium">{user.full_name}</p>
                            {user.id === currentUser?.id && (
                              <span className="text-xs text-muted-foreground">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn('text-xs', ROLE_COLORS[user.role])}
                        >
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary" 
                          className={cn('text-xs', STATUS_COLORS[user.status])}
                        >
                          {user.is_active ? (
                            <CheckCircle size={12} className="mr-1" />
                          ) : (
                            <XCircle size={12} className="mr-1" />
                          )}
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {user.last_login_at 
                          ? formatRelativeDate(user.last_login_at)
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetModal(user)}
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key size={16} />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(user)}
                              className={user.is_active ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}
                              data-testid={`toggle-status-${user.id}`}
                            >
                              {user.is_active ? (
                                <ShieldSlash size={16} />
                              ) : (
                                <ShieldCheck size={16} />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden p-3">
            {loading ? (
              <div className="text-center py-8">
                <ArrowsClockwise size={24} className="animate-spin mx-auto text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users size={32} className="mx-auto text-muted-foreground opacity-50" />
                <p className="mt-2 text-sm text-muted-foreground">No users found</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <MobileUserCard
                  key={user.id}
                  user={user}
                  currentUser={currentUser}
                  onEdit={openEditModal}
                  onResetPassword={openResetModal}
                  onToggleStatus={handleToggleStatus}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <UserFormModal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
        isLoading={isSubmitting}
      />

      <ResetPasswordModal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onReset={handleResetPassword}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default UserManagement;
